import {
  getAdminGroupRulesControllerGetOneQueryKey,
  getAdminGroupRulesControllerListQueryKey,
  useAdminGroupRulesControllerCreate,
  useAdminGroupRulesControllerGetOne,
  useAdminGroupRulesControllerList,
  useAdminGroupRulesControllerRemove,
  useAdminGroupRulesControllerSetMembers,
  useAdminGroupRulesControllerUpdate,
} from '@app/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { KEY_SITE_PROGRESS_ROOT } from '../../progression/hooks/useProgression.js';
import { ADMIN_USERS_LIST_MAX_PAGE, fetchAdminUsers } from '../api';
import { USERS_KEY } from './AdminUsersTable';

function userLabel(u: { alias: string; firstName: string | null; lastName: string | null }) {
  const n = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return n || u.alias;
}

export function AdminGroupsPanel() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [editName, setEditName] = useState('');
  const [memberSelection, setMemberSelection] = useState<Set<string>>(new Set());
  const [membersDirty, setMembersDirty] = useState(false);

  const { data: groups = [], isLoading: loadingGroups } = useAdminGroupRulesControllerList();

  const { data: detail, isLoading: loadingDetail } = useAdminGroupRulesControllerGetOne(
    selectedId ?? '',
    { query: { enabled: Boolean(selectedId) } },
  );

  const { data: usersPage } = useQuery({
    queryKey: USERS_KEY,
    queryFn: () => fetchAdminUsers(ADMIN_USERS_LIST_MAX_PAGE),
  });
  const allUsers = usersPage?.data ?? [];

  useEffect(() => {
    if (!detail) return;
    setEditName(detail.groupRule.name);
    setMemberSelection(new Set(detail.members.map((m) => m.id)));
    setMembersDirty(false);
  }, [detail]);

  const createMut = useAdminGroupRulesControllerCreate({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: getAdminGroupRulesControllerListQueryKey() });
        void qc.invalidateQueries({ queryKey: KEY_SITE_PROGRESS_ROOT });
        setCreateOpen(false);
        setNewName('');
      },
    },
  });

  const updateMut = useAdminGroupRulesControllerUpdate({
    mutation: {
      onSuccess: (_, vars) => {
        void qc.invalidateQueries({ queryKey: getAdminGroupRulesControllerListQueryKey() });
        void qc.invalidateQueries({
          queryKey: getAdminGroupRulesControllerGetOneQueryKey(vars.id),
        });
      },
    },
  });

  const removeMut = useAdminGroupRulesControllerRemove({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: getAdminGroupRulesControllerListQueryKey() });
        void qc.invalidateQueries({ queryKey: KEY_SITE_PROGRESS_ROOT });
        if (selectedId) {
          void qc.removeQueries({
            queryKey: getAdminGroupRulesControllerGetOneQueryKey(selectedId),
          });
        }
        setSelectedId(null);
      },
    },
  });

  const membersMut = useAdminGroupRulesControllerSetMembers({
    mutation: {
      onSuccess: (_data, vars) => {
        void qc.invalidateQueries({ queryKey: getAdminGroupRulesControllerListQueryKey() });
        void qc.invalidateQueries({
          queryKey: getAdminGroupRulesControllerGetOneQueryKey(vars.id),
        });
        setMembersDirty(false);
      },
    },
  });

  const selectedSummary = useMemo(() => {
    if (!selectedId || !detail) return null;
    return detail.groupRule;
  }, [selectedId, detail]);

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,280px)_1fr]">
      <aside className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-800">Groupes</h3>
          <button
            type="button"
            onClick={() => {
              setNewName('');
              setCreateOpen(true);
            }}
            className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700"
          >
            Nouveau
          </button>
        </div>

        {loadingGroups ? (
          <p className="text-sm text-slate-500">Chargement…</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-slate-500">
            Aucun groupe. Créez-en un pour regrouper des collaborateurs.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white shadow-sm">
            {groups.map((g) => (
              <li key={g.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(g.id)}
                  className={`flex w-full items-center justify-between px-4 py-3 text-left text-sm transition hover:bg-slate-50 ${
                    selectedId === g.id
                      ? 'bg-teal-50/80 font-medium text-teal-900'
                      : 'text-slate-800'
                  }`}
                >
                  <span className="truncate">{g.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="min-h-[320px] rounded-3xl border border-slate-200 bg-white p-6 shadow-sm ring-1 ring-slate-100/80">
        {!selectedId ? (
          <p className="text-sm text-slate-500">
            Sélectionnez un groupe pour gérer son nom et ses membres.
          </p>
        ) : loadingDetail ? (
          <p className="text-sm text-slate-500">Chargement du groupe…</p>
        ) : !selectedSummary ? (
          <p className="text-sm text-rose-600">Groupe introuvable.</p>
        ) : (
          <div className="space-y-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-3">
                <label
                  htmlFor="group-name"
                  className="block text-xs font-semibold uppercase tracking-wide text-slate-500"
                >
                  Nom du groupe
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    id="group-name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="min-w-[200px] flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none ring-teal-500/25 focus:ring-2"
                    maxLength={100}
                  />
                  <button
                    type="button"
                    disabled={
                      updateMut.isPending ||
                      !editName.trim() ||
                      editName.trim() === selectedSummary.name
                    }
                    onClick={() =>
                      updateMut.mutate({
                        id: selectedId,
                        data: { name: editName.trim() },
                      })
                    }
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Enregistrer le nom
                  </button>
                </div>
                {updateMut.isError && (
                  <p className="text-sm text-rose-600">
                    {(updateMut.error as Error)?.message ?? 'Erreur lors de la mise à jour'}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (
                    typeof window !== 'undefined' &&
                    window.confirm(
                      `Supprimer le groupe « ${selectedSummary.name} » ? Les droits documentaires qui le ciblent seront aussi supprimés.`,
                    )
                  ) {
                    removeMut.mutate({ id: selectedId });
                  }
                }}
                disabled={removeMut.isPending}
                className="shrink-0 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-800 hover:bg-rose-100 disabled:opacity-50"
              >
                Supprimer
              </button>
            </div>

            <div>
              <div className="mb-3 flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-slate-800">Membres</h4>
                {membersDirty && (
                  <span className="text-xs font-medium text-amber-700">
                    Modifications non enregistrées
                  </span>
                )}
              </div>
              <div className="max-h-[min(420px,50vh)] overflow-y-auto rounded-2xl border border-slate-100 bg-slate-50/50 p-3">
                {allUsers.length === 0 ? (
                  <p className="text-sm text-slate-500">Aucun utilisateur chargé.</p>
                ) : (
                  <ul className="space-y-1">
                    {allUsers.map((u) => (
                      <li key={u.id}>
                        <label className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-white">
                          <input
                            type="checkbox"
                            checked={memberSelection.has(u.id)}
                            onChange={(e) => {
                              setMemberSelection((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(u.id);
                                else next.delete(u.id);
                                return next;
                              });
                              setMembersDirty(true);
                            }}
                            className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm text-slate-800">{userLabel(u)}</span>
                          <span className="text-xs text-slate-400">{u.alias}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                disabled={!membersDirty || membersMut.isPending}
                onClick={() => {
                  if (!selectedId) return;
                  membersMut.mutate({
                    id: selectedId,
                    data: { userIds: [...memberSelection] },
                  });
                }}
                className="mt-4 rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:opacity-50"
              >
                {membersMut.isPending ? 'Enregistrement…' : 'Enregistrer les membres'}
              </button>
              {membersMut.isError && (
                <p className="mt-2 text-sm text-rose-600">
                  {(membersMut.error as Error)?.message ?? 'Erreur enregistrement membres'}
                </p>
              )}
            </div>
          </div>
        )}
      </section>

      {createOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && setCreateOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setCreateOpen(false)}
        >
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl ring-1 ring-slate-200">
            <h3 className="text-lg font-semibold text-slate-900">Nouveau groupe</h3>
            <p className="mt-1 text-sm text-slate-600">Nom interne (100 caractères max), unique.</p>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ex. Rédacteurs audit"
              className="mt-4 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none ring-teal-500/25 focus:ring-2"
              maxLength={100}
            />
            {createMut.isError && (
              <p className="mt-2 text-sm text-rose-600">
                {(createMut.error as Error)?.message ?? 'Erreur à la création'}
              </p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                className="rounded-xl px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={createMut.isPending || !newName.trim()}
                onClick={() => createMut.mutate({ data: { name: newName.trim() } })}
                className="rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
